/**
 * Our maps: OpenStreetMap on MapLibre GL JS, over our own tiles and style on the CDN
 * (built in montanhas-admin-api/map/; the OSM plan, proposals/osm/plan.md). No key and no
 * third party: the style, the PMTiles files, the fonts and the sprites are all ours.
 *
 * MapLibre and the pmtiles protocol are fetched the first time a map is drawn, never before:
 * most pages have none.
 */
type Libre = typeof import("maplibre-gl");

/**
 * The style's address. Development reads the live one too; to try a style not uploaded yet,
 * put `NEXT_PUBLIC_MAP_STYLE_URL=http://localhost:8686/<name>/style.local.json` in
 * `.env.local` and run montanhas-admin-api/map/serve.mjs.
 */
export const MAP_STYLE_URL = process.env.NEXT_PUBLIC_MAP_STYLE_URL || "https://cdn.montanhasdentro.pt/map/outdoor-20261022/style.json";

let loading: Promise<Libre> | null = null;

/** MapLibre with the pmtiles protocol registered, once a page; what react-map-gl's `mapLib` takes */
export function loadMapLibre(): Promise<Libre> {
  loading ??= Promise.all([import("maplibre-gl"), import("pmtiles")])
    .then(([module, { Protocol }]) => {
      const libre = ((module as unknown as { default?: Libre }).default ?? module) as Libre;
      const protocol = new Protocol();
      libre.addProtocol("pmtiles", async (params, abort) => {
        const answer = await protocol.tilev4(params, abort);
        // a raster tile the file does not hold (the sea in the terrain file) answers no data,
        // which leaves MapLibre waiting for it for ever: near the coast a map would never
        // say it had loaded
        if (answer.data == null) throw new Error("No tile here");
        return answer;
      });
      return libre;
    })
    .catch((e) => {
      loading = null;
      throw e;
    });
  return loading;
}

/** where the map opens before it has anything to fit: mainland Portugal (MapLibre's zoom, one less than Google's) */
export const PORTUGAL_CENTER = { lat: 39.6, lng: -8.1 };
export const PORTUGAL_ZOOM = 6;

/** a pin in the brand's green, its point at the bottom centre */
const pinIcon = (colour: string) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40"><path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.7 23.3 0 15 0z" fill="${colour}"/><circle cx="15" cy="15" r="6" fill="#fff"/></svg>`,
  )}`;

export const PIN = pinIcon("#16a34a");

/** MapLibre's own words, in Portuguese */
export const MAP_LOCALE = {
  "CooperativeGesturesHandler.WindowsHelpText": "Usa Ctrl + roda do rato para aproximar o mapa",
  "CooperativeGesturesHandler.MacHelpText": "Usa ⌘ + roda do rato para aproximar o mapa",
  "CooperativeGesturesHandler.MobileHelpText": "Usa dois dedos para mover o mapa",
  "NavigationControl.ZoomIn": "Aproximar",
  "NavigationControl.ZoomOut": "Afastar",
  "AttributionControl.ToggleAttribution": "Créditos do mapa",
};

/**
 * Arrows along a route, in the direction it is walked: the order its points were recorded.
 * Sparse, and only from where a trail is long enough on screen to carry them. The same as the
 * back-office's (`components/map/arrows.ts` in montanhas-admin-web).
 */
export const ARROW_IMAGE = "route-arrow";
/** MapLibre's zoom from which the arrows show */
const ARROWS_FROM = 10;

/**
 * An open chevron pointing along +x, as a signed distance field (MapLibre's SDF icons: the edge at
 * alpha 0.75, a screen pixel of distance an eighth of it), so a layer can draw it white with a halo
 * in the line's colour: on the line, it reads as a notch the line points through.
 */
function chevron(pixelRatio: number) {
  const size = Math.round(18 * pixelRatio);
  const data = new Uint8ClampedArray(size * size * 4);
  const c = size / 2;
  const arm = 5 * pixelRatio;
  const half = 1.25 * pixelRatio;
  // the two arms, meeting at the tip
  const tip: [number, number] = [c + arm / 2, c];
  const ends: [number, number][] = [
    [c - arm / 2, c - arm],
    [c - arm / 2, c + arm],
  ];
  const toSegment = (x: number, y: number, [ax, ay]: [number, number], [bx, by]: [number, number]) => {
    const dx = bx - ax;
    const dy = by - ay;
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy)));
    return Math.hypot(x - ax - t * dx, y - ay - t * dy);
  };
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = Math.min(...ends.map((end) => toSegment(x + 0.5, y + 0.5, end, tip))) - half;
      const i = (y * size + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.max(0, Math.min(255, Math.round(255 * (0.75 - d / (8 * pixelRatio)))));
    }
  }
  return { width: size, height: size, data };
}

/** the arrow image, once a map */
export function addArrowImage(map: import("maplibre-gl").Map) {
  if (map.hasImage(ARROW_IMAGE)) return;
  const ratio = Math.min(2, Math.ceil(window.devicePixelRatio || 1));
  map.addImage(ARROW_IMAGE, chevron(ratio), { sdf: true, pixelRatio: ratio });
}

/** a layer of arrows along the lines of `source`: white, ringed in `colour`, over the line it follows */
export const arrowLayer = (id: string, source: string, colour: string) =>
  ({
    id,
    type: "symbol",
    source,
    minzoom: ARROWS_FROM,
    layout: {
      "symbol-placement": "line",
      "symbol-spacing": 100,
      "icon-image": ARROW_IMAGE,
      "icon-rotation-alignment": "map",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "icon-keep-upright": false,
      "icon-size": ["interpolate", ["linear"], ["zoom"], ARROWS_FROM, 0.8, 14, 1],
    },
    paint: { "icon-color": "#ffffff", "icon-halo-color": colour, "icon-halo-width": 1.5 },
  }) satisfies import("maplibre-gl").SymbolLayerSpecification;
